import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Gauge, Sparkles, Lock, TrendingUp, TrendingDown, Minus } from "lucide-react";
import Shell from "@/components/Shell";
import { http } from "@/lib/api";
import { useApp } from "@/lib/store";

const REC_LABEL = { balanced: "In balance", building: "Building", high: "Recovery is lagging" };
const TREND = {
  rising: { icon: TrendingUp, text: "tilting toward load" },
  easing: { icon: TrendingDown, text: "easing" },
  steady: { icon: Minus, text: "steady" },
};
const DIMS = ["Mental", "Temporal", "Frustration"];

export default function LoadRecovery() {
  const nav = useNavigate();
  const { deviceId } = useApp();
  const [d, setD] = useState(null);

  useEffect(() => {
    http.get(`/load-recovery/${deviceId}`).then(({ data }) => setD(data)).catch(() => setD({ error: true }));
  }, [deviceId]);

  return (
    <Shell>
      <div className="px-6 pt-12 pb-28">
        <button data-testid="lr-back" onClick={() => nav(-1)}
          className="inline-flex items-center gap-1 text-[13px] text-[#6B6B6B] hover:text-[#1A1A1A] ss-focus -ml-1">
          <ArrowLeft size={18} strokeWidth={1.5} /> Back
        </button>

        <div className="mt-8 flex items-center gap-2">
          <Gauge size={18} strokeWidth={1.7} className="text-[#C9A227]" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8a6d10]">Load &amp; Recovery</span>
        </div>
        <h1 className="mt-3 font-serif-display text-4xl leading-tight">The instrument panel.</h1>
        <p className="mt-3 text-[14.5px] leading-relaxed text-[#474747]">
          Two readings drawn from your own check-ins: how your load and recovery are balancing, and
          which kind of load you're actually carrying.
        </p>

        {!d && <p className="mt-8 font-mono-ss text-sm text-[#6B6B6B]">Reading your check-ins…</p>}

        {d && d.available === false && (
          <div className="mt-8 ss-card p-6" data-testid="lr-need-more">
            <p className="text-[15px] font-medium">A few more check-ins to go.</p>
            <p className="mt-2 text-[13.5px] text-[#474747]">
              Your Load &amp; Recovery reading unlocks after {d.needed} check-ins — you have {d.count}.
              It gets sharper the more moments you log.
            </p>
            <button data-testid="lr-checkin" onClick={() => nav("/navigate")}
              className="mt-4 w-full bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-3.5 font-medium ss-tap ss-focus">
              Do a check-in
            </button>
          </div>
        )}

        {d && d.available && d.locked && (
          <div className="mt-8 space-y-3" data-testid="lr-locked">
            {[d.recovery_headline, d.signature_headline].map((h, i) => (
              <div key={i} className="ss-card p-5 flex items-start gap-3">
                <Lock size={16} strokeWidth={1.6} className="text-[#6B6B6B] mt-0.5 flex-shrink-0" />
                <p className="text-[14px] leading-relaxed text-[#1A1A1A]">{h}</p>
              </div>
            ))}
            <div className="ss-card p-5">
              <p className="text-[13.5px] leading-relaxed text-[#474747]">
                Plus turns your check-ins into a private instrument panel — your recovery balance and
                your load type over time, so you know which lever actually helps.
              </p>
              <button data-testid="lr-upgrade" onClick={() => nav("/plus")}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-5 py-3.5 font-medium ss-tap ss-focus hover:-translate-y-0.5">
                <Sparkles size={15} strokeWidth={1.8} /> Unlock with Plus
              </button>
            </div>
            <p className="px-1 text-[11.5px] leading-relaxed text-[#9a9a94]">
              Grounded in the Effort–Recovery model and NASA-TLX workload research. Observations from
              what you report — not clinical measures.
            </p>
          </div>
        )}

        {d && d.available && !d.locked && (
          <div className="mt-8 space-y-4">
            {d.evidence && (
              <div className="flex items-center gap-2" data-testid="lr-evidence">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8a6d10]">
                  Evidence: {d.evidence}
                </span>
                <span className="text-[11.5px] text-[#9a9a94]">· drawn from {d.count} check-ins</span>
              </div>
            )}
            {/* Recovery balance */}
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="ss-card p-5" data-testid="lr-recovery">
              <div className="flex items-center justify-between">
                <h2 className="text-[15px] font-semibold">Recovery imbalance</h2>
                <span className={`text-[11px] font-semibold rounded-full px-2.5 py-0.5 ${
                  d.recovery.level === "high" ? "bg-[#F3E0DA] text-[#a24a34]"
                  : d.recovery.level === "building" ? "bg-[#F7F2D7] text-[#8a6d10]"
                  : "bg-[#E8F0EA] text-[#2f6b45]"}`}>
                  {REC_LABEL[d.recovery.level]}
                </span>
              </div>
              {(() => {
                const l = d.recovery.load_count, r = d.recovery.recovery_count;
                const pct = Math.round((l / Math.max(1, l + r)) * 100);
                const T = TREND[d.recovery.trend].icon;
                return (
                  <>
                    <div className="mt-4 h-2.5 w-full rounded-full overflow-hidden flex bg-[#EBE9E4]">
                      <div className="h-full bg-[#C97b5a]" style={{ width: `${pct}%` }} />
                      <div className="h-full bg-[#7fa98c]" style={{ width: `${100 - pct}%` }} />
                    </div>
                    <div className="mt-2 flex justify-between text-[11.5px] text-[#6B6B6B]">
                      <span>Load · {l}</span>
                      <span className="inline-flex items-center gap-1">
                        <T size={13} strokeWidth={1.8} /> {TREND[d.recovery.trend].text}
                      </span>
                      <span>Recovery · {r}</span>
                    </div>
                  </>
                );
              })()}
              <p className="mt-4 text-[13.5px] leading-relaxed text-[#474747]">{d.recovery.summary}</p>
            </motion.div>

            {/* Load Signature */}
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }}
              className="ss-card p-5" data-testid="lr-signature">
              <div className="flex items-center justify-between">
                <h2 className="text-[15px] font-semibold">Load type</h2>
                <span className="text-[11px] font-semibold rounded-full bg-[#F7F2D7] px-2.5 py-0.5 text-[#8a6d10]">
                  {d.signature.dominant}
                </span>
              </div>
              <div className="mt-4 space-y-2.5">
                {DIMS.map((dim) => {
                  const v = d.signature.mix[dim] || 0;
                  const max = Math.max(1, ...DIMS.map((k) => d.signature.mix[k] || 0));
                  const isDom = d.signature.dominant === dim;
                  return (
                    <div key={dim} className="flex items-center gap-3">
                      <span className="w-[86px] text-[12.5px] text-[#474747]">{dim}</span>
                      <div className="flex-1 h-2 rounded-full bg-[#EBE9E4] overflow-hidden">
                        <div className={`h-full ${isDom ? "bg-[#C9A227]" : "bg-[#CFC9B8]"}`}
                          style={{ width: `${Math.round((v / max) * 100)}%` }} />
                      </div>
                      <span className="w-4 text-right text-[12px] text-[#6B6B6B]">{v}</span>
                    </div>
                  );
                })}
              </div>
              <p className="mt-4 text-[13.5px] leading-relaxed text-[#474747]">{d.signature.summary}</p>
            </motion.div>

            <p className="px-1 text-[11.5px] leading-relaxed text-[#9a9a94]">{d.research_note}</p>
          </div>
        )}
      </div>
    </Shell>
  );
}
